import { Tenant, TenantReference } from 'apprise-core/tenant';
import { Optional } from 'apprise-core/utils';
import { useEnv } from 'apprise-frontend-core/env';
import { useCrud } from 'apprise-ui/utils/crudtask';
import { useTenantClient } from './client';
import { tenantPlural, tenantSingular } from './constants';
import { useTenantClientCache } from './context';
import { TenantChangeEvent } from './events';
import { useTenantModel } from './model';
import { useOracle } from 'apprise-frontend-iam/authz/oracle';


export const useTenantStore = () => {

    const state = useTenantClientCache()

    const oracle = useOracle()
    const calls = useTenantClient()

    const model = useTenantModel()
    const env = useEnv()

    const crud = useCrud({ singular: tenantSingular, plural: tenantPlural })

    const self = {


        all: () => state.get().all

        ,

        allSorted: () => [...state.get().all].sort(model.comparator)


        ,

        allRefs: () => state.get().allRefs


        ,

        lookup: (id: Optional<TenantReference>, useRefFallback = true) => id ? [...self.all(), ...useRefFallback ? self.allRefs() : []].find(t => t.id === id) : undefined

        ,

        lookupByCode: (code: Optional<string>, useRefFallback = true) => code ? [...self.all(), ...useRefFallback ? self.allRefs() : []].find(t => t.code === code) : undefined

        ,

        safeLookup: (id: Optional<TenantReference>) => self.lookup(id) ?? model.unknownTenant(id)

        ,

        fetchAll: crud.fetchAllWith(async () => {

            const [tenants, refs] = await Promise.all(

                oracle.isGuest() ? [[], calls.fetchAllRefs()] :
                    oracle.isTenantUser() ? [calls.fetchAllTenants(), calls.fetchAllRefs()] :
                        [calls.fetchAllTenants()]

            )



            state.set(s => {

                s.all = tenants
                s.allRefs = refs?.sort(model.comparator) ?? []
            })

            return tenants

        }).done()

        ,

        fetchOne: crud.fetchOneWith(calls.fetchOne).done()

        ,

        save: crud.saveOneWith(async (tenant: Tenant) => {

            const isNew = !tenant.lifecycle.created

            const saved = isNew ? await calls.add(tenant) : await calls.update(tenant)

            state.set(s => s.all = isNew ? [saved, ...s.all] : s.all.map(t => t.id === saved.id ? saved : t))

            return saved

        }).done()

        ,


        push: (event: TenantChangeEvent) => {

            const pushed = event.tenant

            state.set(s => {

                switch (event.type) {

                    case 'add': s.all = [...self.all(), pushed]; break;
                    case 'remove': s.all = s.all.filter(t => t.id !== pushed.id); break;
                    case 'change': s.all = s.all.map(t => t.id === pushed.id ? pushed : t); break;

                }

            })


        }

        ,

        removeSilently: crud.removeOneWith(async (tenant: string) => {

            await calls.delete(tenant)

            state.set(s => s.all = s.all.filter(c => c.id !== tenant))


        }).done()

        ,


        removeMany: crud.removeManyWith(async (list: TenantReference[], onConfirm?: () => void) => {

            if (env.name !== 'development')
                throw Error("this is a dev-only operation")

            list.forEach(calls.delete) // best effort

            state.set(s => s.all = s.all.filter(t => !list.includes(t.id)))

            onConfirm?.()

        }).withConsent() //dev-only operation

    }

    return self


}